import moment from "moment-timezone";

/* Work report — the plain-language model.

   Answers one question: **what work actually happened?**

   Two rules govern the wording:

   1. Plain words. "1 hour 35 min", not "1h35". "9:12am", not "09:12". Whole
      hours get spelled out. The reader is an executive skimming for twenty
      seconds, not an analyst reading a chart.

   2. Nothing about how the activity was observed. The source data comes from
      periodic captures, but a report that talks about captures reads like
      surveillance and buries the answer. We surface what was *done*.

   The one piece of real logic in here is working blocks. A naive "first hour to
   last hour" span is wrong for anyone who works split shifts or past midnight —
   it reports the eleven hours they were asleep as missing data, which is both
   alarming and useless. Contiguous stretches are grouped instead, and only gaps
   *inside* a stretch count as unexplained. */

export const IST = "Asia/Kolkata";

// A stretch of work broken by more than this is a separate block, not a hole.
const BRIDGE_HOURS = 2;

/* Shared category vocabulary + colours. Kept here so the bars, the per-item
   composition strips and the legend can never drift apart. */
export const KIND = {
  Development: "#5B5BD6",
  Review: "#8B5CF6",
  Communication: "#0EA5E9",
  Research: "#14B8A6",
  Testing: "#F59E0B",
  Planning: "#EC4899",
  Documentation: "#10B981",
};
const KIND_FALLBACK = ["#5B5BD6", "#0EA5E9", "#10B981", "#F59E0B", "#EC4899", "#8B5CF6", "#14B8A6"];
export const kindColor = (name, i = 0) => KIND[name] || KIND_FALLBACK[i % KIND_FALLBACK.length];

/* "9:12am" — with midnight and noon spelled, because "12:00am to 12:00am" is
   the kind of label that makes people distrust the whole page. */
export function fmt12(ms) {
  const m = moment(ms).tz(IST);
  if (m.hour() === 0 && m.minute() === 0) return "midnight";
  if (m.hour() === 12 && m.minute() === 0) return "noon";
  return m.format("h:mma");
}

// "1 hour 35 min" · "41 min" · "2 hours"
export function fmtHuman(mins) {
  const m = Math.max(0, Math.round(mins || 0));
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const r = m % 60;
  const hLabel = `${h} hour${h === 1 ? "" : "s"}`;
  return r ? `${hLabel} ${r} min` : hLabel;
}

const WORDS = ["no", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine", "ten"];
const spell = (n) => WORDS[n] || String(n);
const cap = (s) => s.replace(/^./, (c) => c.toUpperCase());

export const dayMs = (date, hhmm) =>
  moment.tz(`${date} ${hhmm}`, "YYYY-MM-DD HH:mm", IST).valueOf();

export const hourMs = (date, h) =>
  moment.tz(date, "YYYY-MM-DD", IST).startOf("day").add(h, "hour").valueOf();

/* "09:12–10:47" → {startMs, endMs}. The backend emits an en-dash; tolerate the
   other dashes and an open-ended "ongoing" too. */
export function parseRange(date, timeRange) {
  if (!date || !timeRange) return null;
  const parts = String(timeRange).split(/[–—−-]/);
  const a = /(\d{1,2}):(\d{2})/.exec(parts[0] || "");
  if (!a) return null;
  const startMs = dayMs(date, `${a[1].padStart(2, "0")}:${a[2]}`);
  if (!Number.isFinite(startMs)) return null;
  const b = /(\d{1,2}):(\d{2})/.exec(parts[1] || "");
  let endMs = b ? dayMs(date, `${b[1].padStart(2, "0")}:${b[2]}`) : null;
  if (endMs != null && endMs <= startMs) endMs += 24 * 3600000;
  return { startMs, endMs };
}

// Which clock hours a piece of work touches — used to fetch its step-by-step.
function slotsSpanned(startMs, endMs) {
  const out = [];
  let cur = moment(startMs).tz(IST).startOf("hour");
  const last = moment(endMs).tz(IST);
  while (cur.isBefore(last)) {
    const h = cur.hour();
    out.push(`${String(h).padStart(2, "0")}:00-${String((h + 1) % 24).padStart(2, "0")}:00`);
    cur = cur.add(1, "hour");
  }
  return out;
}

export function buildWorkReport({
  date, memberName, slots = [], metrics = null, report = null, hourReports = {},
}) {
  // ── Minutes per clock hour ──
  const worked = new Map();
  const hourProjects = new Map();
  slots.forEach((s) => {
    const h = parseInt(String(s.hourSlot || s.startTime || "").slice(0, 2), 10);
    if (!Number.isFinite(h)) return;
    const mins = Math.max(0, Math.round(s.duration || 0));
    worked.set(h, (worked.get(h) || 0) + mins);
    if (s.projects?.length) hourProjects.set(h, s.projects);
  });

  // Full 24-hour shape — the day rhythm strip reads straight off this.
  const rhythm = Array.from({ length: 24 }, (_, h) => ({
    hour: h,
    minutes: worked.get(h) || 0,
    label: fmt12(hourMs(date, h)),
    projects: hourProjects.get(h) || [],
  }));

  const activeHours = rhythm.filter((r) => r.minutes > 0);
  const workedFromHours = activeHours.reduce((s, r) => s + r.minutes, 0);

  /* The chart shows the hours that contain work, plus one either side for
     breathing room — not a fixed 12am–12am. Rendering ten empty overnight hours
     halves the width available to the bars that carry the information, and
     makes the card taller for nothing. The exact span is stated in words right
     above the chart, so nothing is lost by cropping. */
  const chartFrom = activeHours.length ? Math.max(0, activeHours[0].hour - 1) : 9;
  const chartTo = activeHours.length ? Math.min(24, activeHours[activeHours.length - 1].hour + 2) : 19;
  const chartHours = rhythm.slice(chartFrom, chartTo);

  const activeMin = metrics?.activeHours != null ? Math.round(metrics.activeHours * 60) : workedFromHours;
  const totalMin = metrics?.totalHours != null ? Math.round(metrics.totalHours * 60) : workedFromHours;
  const awayMin = Math.max(0, totalMin - activeMin);
  const workedMin = activeMin || workedFromHours;

  /* ── Working blocks. Consecutive worked hours more than BRIDGE_HOURS apart
        belong to different stretches; the space between them is simply time
        off, not a hole in the record. ── */
  const blocks = [];
  activeHours.forEach((r) => {
    const last = blocks[blocks.length - 1];
    if (last && r.hour - last.endHour <= BRIDGE_HOURS) last.endHour = r.hour + 1;
    else blocks.push({ startHour: r.hour, endHour: r.hour + 1 });
  });
  blocks.forEach((b) => {
    b.startMs = hourMs(date, b.startHour);
    b.endMs = hourMs(date, b.endHour);
    b.label = `${fmt12(b.startMs)} to ${fmt12(b.endMs)}`;
    b.minutes = rhythm
      .filter((r) => r.hour >= b.startHour && r.hour < b.endHour)
      .reduce((s, r) => s + r.minutes, 0);
  });

  const spanLabel = blocks.length
    ? blocks.map((b) => `${fmt12(b.startMs)}–${fmt12(b.endMs)}`).join(", then ")
    : null;

  // Quiet stretches INSIDE a working block — the only ones worth explaining.
  const quietPeriods = [];
  blocks.forEach((b) => {
    let run = null;
    for (let h = b.startHour; h < b.endHour; h += 1) {
      if ((worked.get(h) || 0) > 0) {
        if (run) { quietPeriods.push(run); run = null; }
        continue;
      }
      if (run) run.endHour = h + 1;
      else run = { startHour: h, endHour: h + 1 };
    }
    if (run) quietPeriods.push(run);
  });
  quietPeriods.forEach((q) => {
    q.label = `${fmt12(hourMs(date, q.startHour))} to ${fmt12(hourMs(date, q.endHour))}`;
    q.minutes = (q.endHour - q.startHour) * 60;
  });
  const quietMin = quietPeriods.reduce((s, q) => s + q.minutes, 0);

  const busiest = activeHours.reduce((best, r) => (!best || r.minutes > best.minutes ? r : best), null);

  // ── The work itself ──
  const items = (report?.sessions || [])
    .map((s, i) => {
      const r = parseRange(date, s.timeRange);
      if (!r) return null;
      const endMs = r.endMs != null ? r.endMs : r.startMs + 3600000;
      const minutes = Math.round((endMs - r.startMs) / 60000);
      const acts = s.activities || [];
      const weightTotal = acts.reduce((x, a) => x + (a.weight || 0), 0) || 1;

      return {
        id: `item-${i}`,
        n: i + 1,
        title: s.title || `Piece of work ${i + 1}`,
        summary: s.summary || "",
        project: s.project && s.project !== "General" ? s.project : null,
        startMs: r.startMs,
        endMs,
        minutes,
        whenLabel: `${fmt12(r.startMs)} – ${fmt12(endMs)}`,
        durationLabel: fmtHuman(minutes),
        // `detail` is the concrete part — file names, ticket numbers — and it is
        // the single most valuable string in the whole payload.
        work: [...acts]
          .sort((a, b) => (b.weight || 0) - (a.weight || 0))
          .map((a, k) => ({
            label: a.label || "Work",
            detail: a.detail || "",
            share: (a.weight || 0) / weightTotal,
            pct: Math.round(((a.weight || 0) / weightTotal) * 100),
            color: kindColor(a.label, k),
          })),
        kind: acts.length
          ? [...acts].sort((a, b) => (b.weight || 0) - (a.weight || 0))[0].label
          : null,
        tools: s.apps || [],
        slots: slotsSpanned(r.startMs, endMs),
        raw: s,
      };
    })
    .filter(Boolean)
    .sort((a, b) => a.startMs - b.startMs)
    .map((it, i) => ({ ...it, n: i + 1, color: kindColor(it.kind, i) }));

  // ── Step-by-step, attached to the item whose window it falls in ──
  Object.entries(hourReports).forEach(([slot, payload]) => {
    (payload?.report?.segments || []).forEach((sg) => {
      const r = parseRange(date, sg.timeRange);
      if (!r) return;
      const end = r.endMs != null ? r.endMs : r.startMs + 300000;
      const step = {
        startMs: r.startMs,
        endMs: end,
        whenLabel: `${fmt12(r.startMs)} – ${fmt12(end)}`,
        title: sg.title || "Work",
        summary: sg.summary || "",
        tools: sg.apps || [],
        kind: sg.category || null,
        color: kindColor(sg.category),
        slot,
      };
      const owner =
        items.find((it) => r.startMs >= it.startMs && r.startMs < it.endMs) ||
        items.find((it) => it.slots.includes(slot));
      if (owner) {
        owner.steps = owner.steps || [];
        owner.steps.push(step);
      }
    });
  });
  items.forEach((it) => { if (it.steps) it.steps.sort((a, b) => a.startMs - b.startMs); });

  // ── Where the time went ──
  const breakdown = (metrics?.projectBreakdown || [])
    .map((p) => ({ label: p.name, minutes: Math.round((p.hours || 0) * 60) }))
    .filter((p) => p.minutes > 0);
  const breakdownTotal = breakdown.reduce((s, p) => s + p.minutes, 0) || 1;
  const whereTime = breakdown.map((p, i) => ({
    ...p,
    share: p.minutes / breakdownTotal,
    pct: Math.round((p.minutes / breakdownTotal) * 100),
    color: KIND_FALLBACK[i % KIND_FALLBACK.length],
    durationLabel: fmtHuman(p.minutes),
  }));
  const breakdownIsTools = (metrics?.breakdownKind || "project") === "app";

  // ── What kind of work. Prefer the AI's own split; otherwise derive it from
  //    the per-item weights so the chart still means something. ──
  let kinds = (report?.activityBreakdown || [])
    .filter((k) => (k.percentage || 0) > 0)
    .map((k, i) => ({ label: k.category, pct: Math.round(k.percentage), color: kindColor(k.category, i) }));
  if (!kinds.length && items.length) {
    const acc = new Map();
    items.forEach((it) => it.work.forEach((w) => acc.set(w.label, (acc.get(w.label) || 0) + w.share * it.minutes)));
    const tot = Array.from(acc.values()).reduce((s, v) => s + v, 0) || 1;
    kinds = Array.from(acc.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([label, v], i) => ({ label, pct: Math.round((v / tot) * 100), color: kindColor(label, i) }));
  }
  kinds = kinds.filter((k) => k.pct > 0).slice(0, 6);

  // ── Day-level context. A program cannot honestly be pinned to one piece of
  //    work with this data, so it lives at the day level where it is true. ──
  const toolMax = Math.max(1, ...(metrics?.topApps || []).map((a) => a.minutes || 0));
  const tools = (metrics?.topApps || [])
    .filter((a) => a.minutes > 0)
    .slice(0, 7)
    .map((a) => ({ label: a.name, minutes: a.minutes, share: a.minutes / toolMax, durationLabel: fmtHuman(a.minutes) }));

  const siteMax = Math.max(1, ...(metrics?.topSites || []).map((s) => s.minutes || 0));
  const sites = (metrics?.topSites || [])
    .filter((s) => s.minutes > 0)
    .slice(0, 6)
    .map((s) => ({
      label: s.title || s.domain, domain: s.domain,
      share: s.minutes / siteMax, durationLabel: fmtHuman(s.minutes),
    }));

  const moves = (metrics?.copyFlows || [])
    .slice(0, 5)
    .map((c) => {
      const [from, to] = String(c.flow || "").split(/\s*→\s*/);
      return { from: from || "", to: to || "", count: c.count || 1 };
    })
    .filter((m) => m.from && m.to);

  // ── The opening sentence. Deterministic, plain, nothing it can't back. ──
  const top = whereTime[0];
  const parts = [];
  if (items.length) {
    parts.push(`${cap(spell(items.length))} ${items.length === 1 ? "piece" : "pieces"} of work.`);
  }
  if (top && whereTime.length > 1 && top.share > 0.5) parts.push(`Most of it on ${top.label}.`);
  else if (top && whereTime.length === 1) parts.push(`All of it on ${top.label}.`);
  if (blocks.length > 1) parts.push(`Split across ${spell(blocks.length)} stretches.`);
  if (quietMin >= 60) {
    const whole = quietMin % 60 === 0 ? quietMin / 60 : null;
    const label = whole != null && whole <= 10 ? `${cap(spell(whole))} hour${whole === 1 ? "" : "s"}` : fmtHuman(quietMin);
    parts.push(`${label} in between with nothing recorded.`);
  }

  return {
    hasAny: activeHours.length > 0 || items.length > 0,
    hasWork: items.length > 0,
    memberName: memberName || "This person",
    dateLabel: moment.tz(date, "YYYY-MM-DD", IST).format("dddd, D MMMM"),
    workedMin,
    workedLabel: fmtHuman(workedMin),
    awayMin,
    awayLabel: fmtHuman(awayMin),
    spanLabel,
    blocks,
    rhythm,
    chartHours,
    chartFrom,
    chartTo,
    busiest,
    sentence: parts.join(" "),
    items,
    whereTime,
    breakdownIsTools,
    kinds,
    tools,
    sites,
    moves,
    quietPeriods,
    quietMin,
    counts: {
      items: items.length,
      projects: whereTime.length,
      tools: tools.length,
      blocks: blocks.length,
      activeHours: activeHours.length,
    },
  };
}

export default buildWorkReport;
